import { IWorkerCompensationRate } from 'models/WorkerCompensationRate'
import { IUser } from 'models/User'
import { IOvertimePolicy } from 'models/OvertimePolicy'
import { IBaseReducer } from '../BaseReducer'
import { IBaseGetParams } from '../Global'
import { IState } from '../State'
import { ICity } from '../City'
import { ICounty } from '../County'
import { TypeOrNull } from '../Common'
import { IFollow } from '../Follow'
import { IPropertyType } from '../PropertyType'

export enum PROPERTY_STATUS {
  PENDING,
  APPROVED,
}

export enum PROPERTY_STATUS_LABEL {
  PENDING = 'Pending',
  APPROVED = 'Approved',
}

export enum GEOFENCE_TYPE {
  RADIUS = 'radius',
  POLYGON = 'polygon',
}

export const GEOFENCE_MIN_RADIUS = 30 // in meters
export const CIRCULAR_GEOFENCE_DEFAULT_RADIUS = 0.25 // in miles
export const CIRCULAR_GEOFENCE_MIN_RADIUS = 0.0186 // 30 meters in miles
export const CIRCULAR_GEOFENCE_MAX_RADIUS = 1.5 // in miles
export const CIRCULAR_GEOFENCE_RADIUS_STEP = 0.025 // in miles

// TODO add optional params to interfaces

export interface IPropertyIncludeParams {
  include?:
    | {
        business?: Record<any, any>
        property_type?: Record<any, any>
        property_category?: Record<any, any>
        county?: Record<any, any>
        state?: Record<any, any>
        followed_by_user?: Record<any, any>
        worker_compensation_rate?: Record<any, any>
        active_overtime_policy?: Record<any, any>
      }
    | (
        | 'business'
        | 'property_type'
        | 'property_category'
        | 'business.followed_by_user'
        | 'county'
        | 'state'
        | 'followed_by_user'
        | 'worker_compensation_rate'
        | 'active_overtime_policy'
      )[]
}

export interface IProperty {
  id: number
  owner_id: number
  business_id: number
  property_type_id: number
  property_category_id: number
  state_id: number
  county_id: number
  city_id: number
  invoice_group_id: number
  name: string
  name_original: string
  description: string
  email: string
  phone: string
  rating: number
  code?: string
  wc_code?: string
  has_tax: boolean
  fixed_time: boolean
  commission_percent: number
  timezone: string
  address: string
  latitude: number
  longitude: number
  zip: number
  timetracker_clock_in_only: boolean
  credits: number
  status: PROPERTY_STATUS
  regions: any[]
  arrival_instructions: string
  arrival_instructions_rich_text: string
  state?: IState
  city?: ICity
  county?: ICounty
  arrival_instructions_attachment_id?: number
  followed_by_user: TypeOrNull<IFollow>
  property_type?: IPropertyType
  worker_compensation_rate?: IWorkerCompensationRate
  property_setting?: IPropertySetting
  active_overtime_policy?: TypeOrNull<IOvertimePolicy>
  polygon_coordinates: ICoordinates[]

  /**
   * Start Deprecated
   */

  owner_full_name: string
  owner_company_name: string
  business_industry_id: number
  property_type_name: string
  property_category_name: string
  credits_amount: string
  business_billing_type_id: string
  suggested_pay_rate: number
  min_wage: string
  /**
   * End Deprecated
   */
}

export enum PROPERTY_SETTING_STATUS {
  INACTIVE,
  ACTIVE,
}

export interface IPropertySetting {
  id: number
  property_id: number
  geofencing_enabled: boolean
  geofencing_web_app_enabled?: boolean
  geofencing_mobile_app_enabled?: boolean
  radius_miles: number
  status?: PROPERTY_SETTING_STATUS
  onsite_tracking_enabled?: boolean
  is_geofence_buffered: boolean
}

export interface ICoordinates {
  lat: number
  lng: number
}

export interface IAddressInfo {
  state_id?: number
  county_id?: number
}

export interface IGetPropertyParamsBase {}

export type IGetPropertyParams = IGetPropertyParamsBase & IPropertyIncludeParams

export interface IGetPropertyAddressInfo {
  address?: string
  zip?: string
}

export interface IGetPropertiesParamsBase extends IBaseGetParams, IGetPropertyParams {
  owner_id?: number
  business_id?: number
  property_type_id?: number
  property_category_id?: number
  country_id?: number
  state_id?: number
  county_id?: number
  city_id?: number
  employee_id?: number
  invoice_group_id?: number
  invoice_id?: number
  status?: PROPERTY_STATUS
  direct_follow?: boolean
  property_id?: number
  blocked_user_id?: IUser['id']
  unblocked_user_id?: IUser['id']
  property_ids?: number[]
  order_by_property_id?: number
}

export type IGetPropertiesParams = IGetPropertiesParamsBase & IPropertyIncludeParams

export interface IPostPropertyParamsBase {
  business_id: number
  property_type_id: number
  name: string
  address: string
  latitude: string
  longitude: string
  zip: string
  owner_id?: number
  property_category?: string
  country_id?: number
  state_id?: number
  county_id?: number
  city_id?: number
  description?: string
  email?: string
  phone?: string
  code?: string
  wc_code?: string
  has_tax?: boolean
  fixed_time?: boolean
  commission_percent?: number
  properties?: number[]
  timetracker_clock_in_only?: boolean
  invoice_group_id?: number
  credits?: number
  status?: PROPERTY_STATUS
  regions?: number[]
  timezone?: string
  onsite_tracking_enabled?: boolean
  polygon_coordinates?: TypeOrNull<string>
  is_geofence_buffered?: boolean
}

export type IPostPropertyParams = IPostPropertyParamsBase & IPropertyIncludeParams

export interface IPutPropertyParamsBase {
  owner_id?: number
  business_id?: number
  property_type_id?: number
  name?: string
  address?: string
  latitude?: string
  longitude?: string
  zip?: string
  property_category?: string
  country_id?: number
  state_id?: number
  county_id?: number
  city_id?: number
  description?: string
  email?: string
  phone?: string
  code?: string
  wc_code?: string
  has_tax?: boolean
  fixed_time?: boolean
  commission_percent?: number
  properties?: number[]
  timetracker_clock_in_only?: boolean
  invoice_group_id?: number
  credits?: number
  status?: PROPERTY_STATUS
  arrival_instructions?: string
  timezone?: string
  regions?: number[]
  arrival_instructions_attachment?: File
  arrival_instructions_attachment_id?: TypeOrNull<number>
  worker_compensation_rate_id?: number
  onsite_tracking_enabled?: boolean
  polygon_coordinates?: TypeOrNull<string>
  is_geofence_buffered?: boolean
}

export type IPutPropertyParams = IPutPropertyParamsBase & IPropertyIncludeParams

export interface IPropertyInfo {
  state_id: number
  country_id: number
}

export interface IGetPropertyInfoParams {
  address: string
  zip: string
}

export interface IInvoiceGroupPropertiesReducer extends IBaseReducer<IProperty[]> {}

export interface IPropertyReducer extends IBaseReducer<IProperty> {}

export interface IPropertiesReducer extends IBaseReducer<IProperty[]> {}

export interface ISelectedShiftPropertyReducer extends IBaseReducer<IProperty> {}
